import React, { useEffect, useMemo, useState } from 'react';
import WidgetShell from './WidgetShell';
import { getStrategySheet } from './strategyDataCache';
import { resolveStrategyDisplayName } from './resolveStrategyDisplayName';
import {
  computeStrategySheetMetrics,
} from '../../utils/strategySheetMetrics';
import {
  extractWeeklyData,
  makeCurrencyFormatters,
} from '../../utils/strategySheetDerivations';

export const KPI_METRIC_OPTIONS = [
  { value: 'netTotal', label: 'Net Total' },
  { value: 'winRate', label: 'Win Rate' },
  { value: 'weekWinRate', label: 'Week Win Rate' },
  { value: 'bestDay', label: 'Best Day' },
  { value: 'worstDay', label: 'Worst Day' },
  { value: 'bestWeek', label: 'Best Week' },
  { value: 'worstWeek', label: 'Worst Week' },
  { value: 'bestMonth', label: 'Best Month' },
  { value: 'worstMonth', label: 'Worst Month' },
  { value: 'totalDays', label: 'Total Days' },
  { value: 'winningWeeks', label: 'Winning Weeks' },
  { value: 'losingWeeks', label: 'Losing Weeks' },
];

/** Matches Vector Results strategy summary cards: green / red / neutral. */
function buildMetricView({ metric, metrics, formatCurrencyValue, contractSize }) {
  if (!metrics) return { value: '—', subtitle: '', valueTone: 'neutral' };
  const winsSum = metrics.totalWinsSum || 0;
  const lossSum = metrics.totalLossSum || 0;
  const totalDays = metrics.totalDays || 0;
  const winningDays = metrics.totalWinningDays || 0;

  switch (metric) {
    case 'netTotal': {
      const net = winsSum + lossSum;
      return {
        value: formatCurrencyValue(net),
        subtitle: `${totalDays} days · scaled to ${contractSize}x default contract size`,
        valueTone: net >= 0 ? 'positive' : 'negative',
      };
    }
    case 'winRate': {
      if (totalDays === 0) return { value: '—', subtitle: 'No trading days', valueTone: 'neutral' };
      const pct = (winningDays / totalDays) * 100;
      return {
        value: `${pct.toFixed(1)}%`,
        // Keep "136 days" together so "days" is not clipped alone at the edge
        subtitle: `${winningDays} of ${totalDays}\u00a0days`,
        valueTone: pct >= 50 ? 'positive' : 'negative',
      };
    }
    case 'weekWinRate': {
      const winning = metrics.winningWeeks || 0;
      const losing = metrics.losingWeeks || 0;
      const decided = winning + losing;
      if (decided === 0) return { value: '—', subtitle: 'No weeks with data', valueTone: 'neutral' };
      const pct = (winning / decided) * 100;
      return {
        value: `${pct.toFixed(1)}%`,
        subtitle: `${winning} of ${decided} weeks`,
        valueTone: pct >= 50 ? 'positive' : 'negative',
      };
    }
    case 'bestDay': {
      const v = metrics.bestDay?.value || 0;
      return {
        value: formatCurrencyValue(v),
        subtitle: metrics.bestDay?.date ? `${metrics.bestDay.label} ${metrics.bestDay.date}` : '',
        valueTone: v >= 0 ? 'positive' : 'negative',
      };
    }
    case 'worstDay': {
      const v = metrics.worstDay?.value || 0;
      return {
        value: formatCurrencyValue(v),
        subtitle: metrics.worstDay?.date ? `${metrics.worstDay.label} ${metrics.worstDay.date}` : '',
        valueTone: v < 0 ? 'negative' : 'positive',
      };
    }
    case 'bestWeek': {
      const v = metrics.bestWeek?.value || 0;
      return {
        value: formatCurrencyValue(v),
        subtitle: metrics.bestWeek?.date ? `Week of ${metrics.bestWeek.date}` : '',
        valueTone: v >= 0 ? 'positive' : 'negative',
      };
    }
    case 'worstWeek': {
      const v = metrics.worstWeek?.value || 0;
      return {
        value: formatCurrencyValue(v),
        subtitle: metrics.worstWeek?.date ? `Week of ${metrics.worstWeek.date}` : '',
        valueTone: v < 0 ? 'negative' : 'positive',
      };
    }
    case 'bestMonth': {
      const v = metrics.bestMonth?.value || 0;
      return {
        value: formatCurrencyValue(v),
        subtitle: metrics.bestMonth?.month || '',
        valueTone: v >= 0 ? 'positive' : 'negative',
      };
    }
    case 'worstMonth': {
      const v = metrics.worstMonth?.value || 0;
      return {
        value: formatCurrencyValue(v),
        subtitle: metrics.worstMonth?.month || '',
        valueTone: v < 0 ? 'negative' : 'positive',
      };
    }
    case 'totalDays':
      return { value: String(totalDays), subtitle: 'Trading days in sheet', valueTone: 'neutral' };
    case 'winningWeeks':
      return { value: String(metrics.winningWeeks || 0), subtitle: 'Positive weeks', valueTone: 'positive' };
    case 'losingWeeks':
      return { value: String(metrics.losingWeeks || 0), subtitle: 'Negative weeks', valueTone: 'negative' };
    default:
      return { value: '—', subtitle: '', valueTone: 'neutral' };
  }
}

function KpiCardWidget({ config, user, customCombos }) {
  const {
    strategy = '',
    metric = 'netTotal',
    contractType = 'NQ',
    contractSize = 1,
  } = config || {};

  const [rawRows, setRawRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (!strategy) {
      setRawRows(null);
      setError('Pick a strategy in the widget settings.');
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    getStrategySheet({ strategy, email: user?.email })
      .then((data) => {
        if (cancelled) return;
        setRawRows(Array.isArray(data?.rows) ? data.rows : []);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.message || 'Failed to load strategy data');
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [strategy, user?.email, reloadToken]);

  const metrics = useMemo(() => {
    if (!rawRows) return null;
    return computeStrategySheetMetrics(rawRows);
  }, [rawRows]);

  const { formatCurrencyValue } = useMemo(
    () => makeCurrencyFormatters({ contractType, sheetName: strategy, contractSize: Number(contractSize) || 1 }),
    [contractType, strategy, contractSize],
  );

  const view = useMemo(
    () => buildMetricView({
      metric,
      metrics,
      formatCurrencyValue,
      contractSize: Number(contractSize) || 1,
    }),
    [metric, metrics, formatCurrencyValue, contractSize],
  );

  const metricLabel = KPI_METRIC_OPTIONS.find((m) => m.value === metric)?.label || 'Metric';

  const hasWeeklyData = useMemo(() => extractWeeklyData(rawRows || []).length > 0, [rawRows]);

  const toneClass = view.valueTone === 'positive'
    ? 'dashboard-kpi-strategy-value--positive'
    : view.valueTone === 'negative'
      ? 'dashboard-kpi-strategy-value--negative'
      : '';

  const displayStrategyName = resolveStrategyDisplayName(strategy, customCombos);
  return (
    <WidgetShell
      widgetClassName="dashboard-widget--kpi"
      title={displayStrategyName || 'KPI'}
      subtitle={`${metricLabel} · ${contractType} · ${Number(contractSize) || 1}x default contract size`}
      loading={loading}
      error={error}
      onRetry={() => setReloadToken((t) => t + 1)}
      bodyClassName="dashboard-widget-body-kpi dashboard-widget-body-kpi-vector"
    >
      {metric === 'weekWinRate' ? (
        <WeekdayWinRateRow metrics={metrics} hasWeeklyData={hasWeeklyData} rawRows={rawRows} />
      ) : (
        <div className="dashboard-kpi-strategy-card">
          <div className="dashboard-kpi-strategy-label">
            <span className="dashboard-kpi-strategy-label-text">{metricLabel}</span>
          </div>
          <div className={`dashboard-kpi-strategy-value ${toneClass}`}>{view.value}</div>
          {view.subtitle ? (
            <div className="dashboard-kpi-strategy-meta">{view.subtitle}</div>
          ) : null}
          {!hasWeeklyData && rawRows ? (
            <div className="dashboard-kpi-hint">No daily data found in this strategy sheet.</div>
          ) : null}
        </div>
      )}
    </WidgetShell>
  );
}

const WEEKDAY_KEYS = [
  { key: 'Mon', label: 'Monday' },
  { key: 'Tue', label: 'Tuesday' },
  { key: 'Wed', label: 'Wednesday' },
  { key: 'Thu', label: 'Thursday' },
  { key: 'Fri', label: 'Friday' },
];

function WeekdayWinRateRow({ metrics, hasWeeklyData, rawRows }) {
  const rates = metrics?.weekdayWinRates || {};
  return (
    <div className="dashboard-kpi-weekday-row">
      {WEEKDAY_KEYS.map(({ key, label }) => {
        const pct = Number.isFinite(rates[key]) ? rates[key] : 0;
        const tone = pct >= 50
          ? 'dashboard-kpi-strategy-value--positive'
          : 'dashboard-kpi-strategy-value--negative';
        return (
          <div key={key} className="dashboard-kpi-strategy-card dashboard-kpi-weekday-card">
            {/*
             * Two-part label so mobile CSS can hide the long-form parts and
             * fall back to the 3-letter day code, keeping all five cards
             * visible on a phone without horizontal scroll.
             */}
            <div className="dashboard-kpi-strategy-label">
              <span className="dashboard-kpi-weekday-label-full">{label}</span>
              <span className="dashboard-kpi-weekday-label-short">{key}</span>
              <span className="dashboard-kpi-weekday-label-suffix"> Win Rate</span>
            </div>
            <div className={`dashboard-kpi-strategy-value ${tone}`}>{pct.toFixed(1)}%</div>
            <div className="dashboard-kpi-strategy-meta">Wins / total days with data</div>
          </div>
        );
      })}
      {!hasWeeklyData && rawRows ? (
        <div className="dashboard-kpi-hint dashboard-kpi-weekday-hint">
          No daily data found in this strategy sheet.
        </div>
      ) : null}
    </div>
  );
}

export default KpiCardWidget;
